import { useQuery } from '@tanstack/react-query';
import { factorServicePriceKey, fetchFactorServicePrice } from './factorServicePrice';
import { getBooksTelemetry, useBookDetailsQuery } from '@/common/apis/services/booking/getBookDetails';
import { useConsultInvoice } from '@/common/apis/services/factor/consultInvoice';
import { CENTERS } from '@/common/types/centers';
import { useUserInfoStore } from '@/modules/login/store/userInfo';
import { useEffect, useMemo, useState } from 'react';

interface AxiosErrorLike {
  message?: string;
  response?: { status?: number; data?: { message?: string } };
}

export const useInvoice = ({
  bookId,
  centerId,
  serviceId,
  userCenterId,
}: {
  bookId?: string;
  centerId: string;
  serviceId?: string;
  userCenterId?: string;
}) => {
  const isLogin = useUserInfoStore(state => state.isLogin);
  const userPending = useUserInfoStore(state => state.pending);
  const getConsultInvoice = useConsultInvoice();
  const [invoiceDetails, setInvoiceDetails] = useState<{
    totalPrice?: string;
    price?: string;
    tax?: string;
  }>({});

  const serviceInvoice = useQuery(
    factorServicePriceKey(centerId, serviceId, userCenterId, isLogin),
    () => fetchFactorServicePrice(centerId, serviceId!, userCenterId!),
    { enabled: !bookId && !!serviceId && !!userCenterId, staleTime: 30_000, refetchOnWindowFocus: false },
  );

  // The book row comes from the shared, deduped query — the page already fetches the same
  // POST /api/getBooks for doctor info. Only the consult invoice stays a separate call; the
  // consult center reads its price from it, so the hook skips the getBooks observer there.
  const isConsult = centerId === CENTERS.CONSULT;
  const bookDetailsQuery = useBookDetailsQuery({ bookId, enabled: isLogin && !userPending && !isConsult });

  useEffect(() => {
    if (!isConsult || !bookId || !isLogin || userPending) return;
    let alive = true;
    getConsultInvoice
      .mutateAsync({ book_id: bookId })
      .then(res => {
        if (alive)
          setInvoiceDetails({
            totalPrice: res?.data?.result?.book_price,
            price: res?.data?.result?.service_price,
            tax: res?.data?.result?.vat,
          });
      })
      .catch(() => {
        // Price stays empty; the 10s price_not_loaded event carries the diagnostics.
      });
    return () => {
      alive = false;
    };
  }, [isConsult, bookId, isLogin, userPending]);

  const bookRow = bookDetailsQuery.data?.data?.result?.[0];
  const price = isConsult ? invoiceDetails.price : bookRow?.book_payable_cost;
  const totalPrice = isConsult ? invoiceDetails.totalPrice : bookRow?.book_payable_cost;

  // Cause signal for the price_not_loaded event — which leg failed/slowed and how.
  const priceDiagnostics = useMemo(() => {
    const errorMessage = (error: unknown) => {
      const axiosError = error as AxiosErrorLike | undefined;
      return axiosError ? axiosError.response?.data?.message ?? axiosError.message : undefined;
    };
    if (!bookId) {
      return {
        price_source: 'service_price',
        service_price_status: serviceInvoice.status,
        service_price_http_status: (serviceInvoice.error as AxiosErrorLike | undefined)?.response?.status,
        service_price_error: errorMessage(serviceInvoice.error),
      };
    }
    if (isConsult) {
      return {
        price_source: 'consult_invoice',
        consult_invoice_status: getConsultInvoice.status,
        consult_invoice_http_status: (getConsultInvoice.error as AxiosErrorLike | undefined)?.response?.status,
        consult_invoice_error: errorMessage(getConsultInvoice.error),
        consult_invoice_has_price: invoiceDetails.price != null && invoiceDetails.price !== '',
        get_books_has_row: !!bookRow,
      };
    }
    return {
      price_source: 'get_books',
      get_books_status: bookDetailsQuery.status,
      get_books_http_status: getBooksTelemetry.httpStatus,
      get_books_error: getBooksTelemetry.error,
      get_books_duration_ms: getBooksTelemetry.durationMs,
      get_books_attempts: getBooksTelemetry.attempts,
      get_books_has_row: !!bookRow,
      get_books_has_price: price != null && price !== '',
    };
  }, [
    bookId,
    isConsult,
    serviceInvoice.status,
    serviceInvoice.error,
    getConsultInvoice.status,
    getConsultInvoice.error,
    invoiceDetails.price,
    bookDetailsQuery.status,
    bookRow,
    price,
  ]);

  return !bookId && serviceId
    ? { ...serviceInvoice.data, isLoading: serviceInvoice.isLoading, priceDiagnostics }
    : {
        totalPrice,
        price,
        tax: isConsult ? invoiceDetails.tax : undefined,
        isLoading: isConsult ? getConsultInvoice.isLoading : bookDetailsQuery.isFetching,
        priceDiagnostics,
      };
};

export default useInvoice;
